import classNames from "classnames";
import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import 'dayjs/locale/pt-br';
import { useState } from "react";
import { withRouter } from "react-router";
import { Button, CheckPicker, ControlLabel, DatePicker, DateRangePicker, Divider, FormGroup, Notification, Panel } from "rsuite";
import AutomaticSalesPanel from "./components/AutomaticSalesPanel";
import FreePeriodComparisonPanel from "./components/FreePeriodComparisonPanel";
import ChannelPeriodComparisonPanel from "./components/ChannelPeriodComparisonPanel";
import ChannelMonthlyComparisonPanel from "./components/ChannelMonthlyComparisonPanel";
import StoreMonthlyComparisonPanel from "./components/StoreMonthlyComparisonPanel";
import StoreChannelMonthlyComparisonPanel from "./components/StoreChannelMonthlyComparisonPanel";
import AverageTicketByChannelPanel from "./components/AverageTicketByChannelPanel";
import AverageTicketByStorePanel from "./components/AverageTicketByStorePanel";
import { ChannelSelectData } from "../../core/constants/Channels";
import { locale } from "../../utilities/locale";
import axios from "../../utilities/axios";
import { MethodsUtils } from "../../core/utils/MethodsUtils";
dayjs.extend(isSameOrBefore);
dayjs.locale('pt-br');

type MainReadsData = {
    periodo_a: {
        total: number;
        loja_lider: string | null;
        loja_total: number;
        canal_lider: string | null;
        canal_total: number;
    }
    periodo_b: {
        total: number;
        loja_lider: string | null;
        loja_total: number;
        canal_lider: string | null;
        canal_total: number;
    }
    maior_crescimento_loja: string | null;
    maior_crescimento_valor: number;
    maior_queda_loja: string | null;
    maior_queda_valor: number;
}

const SaleAnalyticsPage = () => {
    const [channels, setChannels] = useState<string[]>(() => ChannelSelectData.map(v => v.value) || []);
    const [firstPeriod, setFirstPeriod] = useState<Date[]>(() => [
        dayjs().subtract(1, 'year').startOf('year').toDate(),
        dayjs().subtract(1, 'year').endOf('year').toDate()
    ]);
    const [secondPeriod, setSecondPeriod] = useState<Date[]>(() => [
        dayjs().startOf('year').toDate(),
        dayjs().endOf('year').toDate()
    ]);
    const [searchTrigger, setSearchTrigger] = useState<number>(0);
    const [mainReadsData, setMainReadsData] = useState<MainReadsData>({
        periodo_a: {
            total: 0,
            loja_lider: null,
            loja_total: 0,
            canal_lider: null,
            canal_total: 0
        },
        periodo_b: {
            total: 0,
            loja_lider: null,
            loja_total: 0,
            canal_lider: null,
            canal_total: 0
        },
        maior_crescimento_loja: null,
        maior_crescimento_valor: 0,
        maior_queda_loja: null,
        maior_queda_valor: 0
    });
    const [loading, setLoading] = useState(false);

    const firstPeriodFormatted = [dayjs(firstPeriod[0]).format('YYYY-MM-DD'), dayjs(firstPeriod[1]).format('YYYY-MM-DD')];
    const secondPeriodFormatted = [dayjs(secondPeriod[0]).format('YYYY-MM-DD'), dayjs(secondPeriod[1]).format('YYYY-MM-DD')];

    const handleSearch = async () => {
        if (channels.length === 0) {
            Notification.warning({
                title: 'Atenção',
                description: 'Selecione ao menos um canal para realizar a busca.',
                placement: 'bottomEnd'
            });
            return;
        }
        if (firstPeriod.length !== 2 || secondPeriod.length !== 2) {
            Notification.warning({
                title: 'Atenção',
                description: 'Selecione ambos os períodos para realizar a busca.',
                placement: 'bottomEnd'
            });
            return;
        }

        const firstStart = dayjs(firstPeriod[0]);
        const firstEnd = dayjs(firstPeriod[1]);
        const secondStart = dayjs(secondPeriod[0]);
        const secondEnd = dayjs(secondPeriod[1]);
        if (firstStart.isSameOrBefore(secondEnd) && secondStart.isSameOrBefore(firstEnd)) {
            Notification.error({
                title: 'Erro',
                description: 'Os períodos não podem se sobrepor. Selecione períodos sem conflito.',
                placement: 'bottomEnd'
            });
            return;
        }
        if (secondStart.isSameOrBefore(firstEnd)) {
            Notification.error({
                title: 'Erro',
                description: 'O segundo período precisa ser após o primeiro.',
                placement: 'bottomEnd'
            });
            return;
        }

        setLoading(true);
        try {
            const { data } = await axios.post('/api/v1/analytics/sales/main_reads', {
                channels,
                periods: [firstPeriodFormatted, secondPeriodFormatted]
            });
            setMainReadsData(data)
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
            setSearchTrigger(prev => prev + 1);
        }
    };

    const totalA = mainReadsData.periodo_a.total;
    const totalB = mainReadsData.periodo_b.total;
    const totalVar = totalA === 0 && totalB > 0 ? 100
        : totalB === 0 && totalA > 0 ? -100
            : totalA > 0 ? (totalB - totalA) / totalA * 100 : 0;

    return (
        <>
            <div style={{ padding: '4px 20px 20px', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 12, marginLeft: localStorage.getItem("hiddenSidebar") === "true" ? "0px" : "203px" }} className={classNames("container")}>
                <div style={{ width: '100%', display: 'flex', flexDirection: 'row', gap: 12 }}>
                    <FormGroup style={{ flex: 3 }}>
                        <ControlLabel>Canal</ControlLabel>
                        <CheckPicker
                            block
                            searchable={false}
                            value={channels}
                            onChange={(v) => setChannels(v)}
                            data={ChannelSelectData}
                        />
                    </FormGroup>

                     <FormGroup style={{ flex: 1 }}>
                        <ControlLabel>Primeiro Periodo</ControlLabel>
                        <DateRangePicker
                            style={{ width: '100%' }}
                            placeholder="Selecione..."
                            format="DD/MM/YYYY"
                            ranges={[
                                { label: 'Mês passado', value: [dayjs().subtract(1, 'month').startOf('month').toDate(), dayjs().subtract(1, 'month').endOf('month').toDate()] },
                                { label: `${dayjs().subtract(5, 'month').format('MMM YY')} - ${dayjs().subtract(3, 'month').format('MMM YY')}`, value: [dayjs().subtract(5, 'month').startOf('month').toDate(), dayjs().subtract(3, 'month').endOf('month').toDate()] },
                                { label: 'Ano passado', value: [dayjs().subtract(1, 'year').startOf('year').toDate(), dayjs().subtract(1, 'year').endOf('year').toDate()] },
                            ]}
                            locale={locale}
                            value={firstPeriod as any}
                            onChange={(v) => setFirstPeriod(v as Date[])}
                            placement="bottomEnd"
                            cleanable={false}
                        />
                    </FormGroup>

                    <FormGroup style={{ flex: 1 }}>
                        <ControlLabel>Segundo Periodo</ControlLabel>
                        <DateRangePicker
                            style={{ width: '100%' }}
                            placeholder="Selecione..."
                            format="DD/MM/YYYY"
                            ranges={[
                                { label: 'Mês atual', value: [dayjs().startOf('month').toDate(), dayjs().endOf('month').toDate()] },
                                { label: `${dayjs().subtract(2, 'month').format('MMM YY')} - Mês atual`, value: [dayjs().subtract(2, 'month').startOf('month').toDate(), dayjs().endOf('month').toDate()] },
                                { label: 'Ano atual', value: [dayjs().startOf('year').toDate(), dayjs().endOf('year').toDate()] },
                            ]}
                            locale={locale}
                            value={secondPeriod as any}
                            onChange={(v) => setSecondPeriod(v as Date[])}
                            placement="bottomEnd"
                            cleanable={false}
                        />
                    </FormGroup>

                    <FormGroup style={{ flex: 0.5, display: 'flex', alignItems: 'flex-end' }}>
                        <Button appearance="primary" onClick={handleSearch} style={{ width: '100%' }} loading={loading} disabled={loading}>
                            Buscar
                        </Button>
                    </FormGroup>
                </div>

                <Divider style={{ width: '100%' }} />

                {loading ? (
                    <h6 style={{ textAlign: 'center', width: '100%' }}>Buscando...</h6>
                ) : searchTrigger == 0 ? (
                    <h6 style={{ textAlign: 'center', width: '100%' }}>Clique em buscar para exibir os resultados</h6>
                ) : (
                    <>
                        <div style={{ display: 'flex', flexDirection: 'row', gap: 8, width: '100%' }}>

                            <div style={{ display: 'flex', flexDirection: 'column', flex: 1, border: '1px solid rgb(255, 140, 66)', borderTopWidth: 6, padding: 8, borderRadius: 8, background: 'white' }}>
                                <p style={{ opacity: .7 }}>Maior crescimento</p>
                                <p style={{ fontWeight: 'bold', fontSize: 16, marginTop: 2 }}>{mainReadsData.maior_crescimento_loja ? `${mainReadsData.maior_crescimento_loja} (${MethodsUtils.formatPercentual(mainReadsData.maior_crescimento_valor, { signDisplay: "always" })})` : 'Nenhum'}</p>
                            </div>

                            <div style={{ display: 'flex', flexDirection: 'column', flex: 1, border: '1px solid rgb(255, 140, 66)', borderTopWidth: 6, padding: 8, borderRadius: 8, background: 'white' }}>
                                <p style={{ opacity: .7 }}>Maior queda</p>
                                <p style={{ fontWeight: 'bold', fontSize: 16, marginTop: 2 }}>{mainReadsData.maior_queda_loja ? `${mainReadsData.maior_queda_loja} (${MethodsUtils.formatPercentual(mainReadsData.maior_queda_valor, { signDisplay: "always" })})` : 'Nenhum'}</p>
                            </div>

                            <div style={{ display: 'flex', flexDirection: 'column', flex: 1, border: '1px solid rgb(255, 140, 66)', borderTopWidth: 6, padding: 8, borderRadius: 8, background: 'white' }}>
                                <p style={{ opacity: .7 }}>Total dos períodos</p>
                                <p style={{ fontWeight: 'bold', fontSize: 16, marginTop: 2 }}>
                                    {MethodsUtils.formatCurrency(totalA + totalB)}&nbsp;
                                    ({MethodsUtils.formatCurrency(totalB - totalA, { signDisplay: "always" })}
                                    {" / "}
                                    {MethodsUtils.formatPercentual(totalVar, { signDisplay: "always" })})
                                </p>
                            </div>
                        </div>

                        <Panel bordered bodyFill style={{ background: 'white', borderColor: 'rgb(255, 140, 66)' }}>
                            <h6 style={{ textAlign: 'center', marginTop: 8 }}>Leituras</h6>
                            <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center', height: 200, padding: 20, gap: 8 }}>
                                <div style={{ display: 'flex', flexDirection: 'column', textAlign: 'right', gap: 8 }}>
                                    <h6 style={{ marginBottom: 'auto' }}>Período A</h6>
                                    <div style={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                                        <div style={{ fontWeight: 'bold' }}>Melhor loja</div>
                                        <div>{mainReadsData.periodo_a.loja_lider ? `${mainReadsData.periodo_a.loja_lider} (${MethodsUtils.formatCurrency(mainReadsData.periodo_a.loja_total)})` : 'Nenhum'}</div>
                                    </div>
                                    <div style={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                                        <div style={{ fontWeight: 'bold' }}>Melhor canal</div>
                                        <div>{mainReadsData.periodo_a.canal_lider ? `${mainReadsData.periodo_a.canal_lider} (${MethodsUtils.formatCurrency(mainReadsData.periodo_a.canal_total)})` : 'Nenhum'}</div>
                                    </div>
                                    <div style={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                                        <div style={{ fontWeight: 'bold' }}>Total</div>
                                        <div>{MethodsUtils.formatCurrency(mainReadsData.periodo_a.total)}</div>
                                    </div>
                                </div>
                                <Divider vertical style={{ height: '100%', width: 2, background: 'rgb(255, 140, 66)' }} />
                                <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'left', gap: 8 }}>
                                    <h6 style={{ marginBottom: 'auto' }}>Período B</h6>
                                    <div style={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                                        <div style={{ fontWeight: 'bold' }}>Melhor loja</div>
                                        <div>{mainReadsData.periodo_b.loja_lider ? `${mainReadsData.periodo_b.loja_lider} (${MethodsUtils.formatCurrency(mainReadsData.periodo_b.loja_total)})` : 'Nenhum'}</div>
                                    </div>
                                    <div style={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                                        <div style={{ fontWeight: 'bold' }}>Melhor canal</div>
                                        <div>{mainReadsData.periodo_b.canal_lider ? `${mainReadsData.periodo_b.canal_lider} (${MethodsUtils.formatCurrency(mainReadsData.periodo_b.canal_total)})` : 'Nenhum'}</div>
                                    </div>
                                    <div style={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                                        <div style={{ fontWeight: 'bold' }}>Total</div>
                                        <div>{MethodsUtils.formatCurrency(mainReadsData.periodo_b.total)}</div>
                                    </div>
                                </div>
                            </div>
                        </Panel>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, width: '100%' }}>
                            <AutomaticSalesPanel channels={channels} periods={[firstPeriodFormatted, secondPeriodFormatted]} searchTrigger={searchTrigger} />
                            <FreePeriodComparisonPanel channels={channels} periods={[firstPeriodFormatted, secondPeriodFormatted]} searchTrigger={searchTrigger} />
                            <ChannelPeriodComparisonPanel channels={channels} periods={[firstPeriodFormatted, secondPeriodFormatted]} searchTrigger={searchTrigger} />
                            <ChannelMonthlyComparisonPanel channels={channels} periods={[firstPeriodFormatted, secondPeriodFormatted]} searchTrigger={searchTrigger} />
                            <StoreMonthlyComparisonPanel channels={channels} periods={[firstPeriodFormatted, secondPeriodFormatted]} searchTrigger={searchTrigger} />
                            <StoreChannelMonthlyComparisonPanel channels={channels} periods={[firstPeriodFormatted, secondPeriodFormatted]} searchTrigger={searchTrigger} />
                            <AverageTicketByChannelPanel channels={channels} periods={[firstPeriodFormatted, secondPeriodFormatted]} searchTrigger={searchTrigger} />
                            <AverageTicketByStorePanel channels={channels} periods={[firstPeriodFormatted, secondPeriodFormatted]} searchTrigger={searchTrigger} />
                        </div>
                    </>
                )}
            </div>
        </>
    );
};

export default withRouter(SaleAnalyticsPage);
